import { useEffect, useState } from 'react';

/**
 * Detecta quando há um novo Service Worker esperando para ativar.
 * Retorna `updateAvailable` (boolean) e `applyUpdate` (função que aplica a atualização).
 *
 * Fluxo:
 * 1. Browser baixa o novo SW quando detecta mudança no arquivo
 * 2. Novo SW entra em estado "waiting" (não substitui o antigo ainda)
 * 3. Este hook notifica o app via `updateAvailable = true`
 * 4. Usuário clica em "Atualizar" → `applyUpdate()` envia SKIP_WAITING ao SW
 * 5. Novo SW ativa → `controllerchange` dispara → página recarrega
 */
export function usePwaUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingSW, setWaitingSW] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let registration: ServiceWorkerRegistration | null = null;

    const trackWaiting = (sw: ServiceWorker | null) => {
      if (!sw) return;
      if (sw.state === 'installed') {
        setWaitingSW(sw);
        setUpdateAvailable(true);
        return;
      }
      sw.addEventListener('statechange', () => {
        if (sw.state === 'installed') {
          setWaitingSW(sw);
          setUpdateAvailable(true);
        }
      });
    };

    navigator.serviceWorker.ready.then((reg) => {
      registration = reg;

      // Já havia um SW esperando antes de chegar aqui (ex: aba reaberta)
      if (reg.waiting) {
        trackWaiting(reg.waiting);
      }

      // Detecta quando o browser baixa um novo SW
      reg.addEventListener('updatefound', () => {
        trackWaiting(reg.installing);
      });
    });

    // Quando o SW novo toma o controle, recarrega UMA única vez para aplicar os novos assets.
    // A flag evita loops caso controllerchange dispare repetidamente.
    let reloadIniciado = false;
    const handleControllerChange = () => {
      if (reloadIniciado) return;
      reloadIniciado = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Verifica periodicamente se há atualização (a cada 10 min enquanto online)
    const interval = setInterval(() => {
      if (navigator.onLine && registration) {
        registration.update().catch(() => {});
      }
    }, 10 * 60 * 1000);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      clearInterval(interval);
    };
  }, []);

  const applyUpdate = () => {
    if (!waitingSW) return;
    waitingSW.postMessage({ type: 'SKIP_WAITING' });
  };

  return { updateAvailable, applyUpdate };
}
